/**
 * useDashboardMode — the one accessor every parent surface uses to learn
 * whether it is rendering for a family in memorial mode (#693).
 *
 * `resolveDashboardMode` shipped with #644 and, at HEAD before this change,
 * had exactly one consumer: `DashboardPage.tsx:81`. That single call site was
 * the defect. A bereaved family got a correct memorial dashboard, and then
 * one nav click away their `/tasks` list still said "Pick up Lila's
 * prescription" — the platform asking a bereaved parent to run their dead
 * child's errand, once a day, until a staff member happened to notice.
 *
 * This hook is the "shared successor" #693's acceptance criteria asks for. It
 * adds no new predicate: it wraps the existing `resolveDashboardMode`, over
 * the existing `useFamilyMine` query, so a page gains the gate with one line
 * and cannot invent its own query key, its own phase mapping, or its own idea
 * of which phases count.
 *
 * `isLoading` matters more here than on a normal read. Until the family
 * record lands the mode is the fail-safe `'active'`, which is right for a
 * living patient and wrong for a bereaved one — so a surface that renders
 * treatment-era rows should hold them until the flag resolves rather than
 * flash them and retract. `resolveDashboardMode`'s default protects the
 * far worse error (memorializing a living child); this flag covers the other.
 */
import { resolveDashboardMode, type DashboardMode } from '@/lib/dashboard-mode';
import { useFamilyMine } from '@/hooks/useFamilyMine';

export interface DashboardModeState {
  mode: DashboardMode;
  isMemorial: boolean;
  /** True while the family record is still in flight. */
  isLoading: boolean;
}

export function useDashboardMode(): DashboardModeState {
  const { data, isLoading } = useFamilyMine();
  const mode = resolveDashboardMode(data?.data?.patient?.treatment_phase);
  return { mode, isMemorial: mode === 'memorial', isLoading };
}
